import type { CSSProperties } from 'react';

import type { FormTheme } from '@kushya/shared';

import { cn } from '@/lib/utils';

interface FormCoverProps {
  theme?: FormTheme;
  alignment?: 'left' | 'center' | 'right';
}

const buildCoverStyle = (theme: FormTheme): CSSProperties => {
  const primary = theme.primaryColor ?? '#0f172a';
  const secondary = theme.secondaryColor ?? theme.accentColor ?? '#38bdf8';
  const coverStyle = theme.coverStyle ?? 'none';

  if (coverStyle === 'solid') {
    return {
      backgroundColor: theme.coverColor ?? primary,
    };
  }

  if (coverStyle === 'gradient') {
    const from = theme.coverGradientFrom ?? primary;
    const to = theme.coverGradientTo ?? secondary;
    return {
      backgroundImage: `linear-gradient(120deg, ${from}, ${to})`,
    };
  }

  if (coverStyle === 'image' && theme.coverImageUrl) {
    return {
      backgroundImage: `url(${theme.coverImageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }

  return {
    backgroundImage: `linear-gradient(120deg, ${primary}, ${secondary})`,
  };
};

export function FormCover({ theme, alignment = 'center' }: FormCoverProps) {
  const coverStyle = theme?.coverStyle ?? 'none';
  const icon = theme?.icon?.trim();
  const hasCover = coverStyle !== 'none';

  if (!theme || (!hasCover && !icon)) {
    return null;
  }

  const iconAlignment =
    alignment === 'center'
      ? 'left-1/2 -translate-x-1/2'
      : alignment === 'right'
        ? 'right-6'
        : 'left-6';
  const iconContainerAlignment =
    alignment === 'center'
      ? 'justify-center'
      : alignment === 'right'
        ? 'justify-end'
        : 'justify-start';

  return (
    <div className="mb-6">
      {hasCover ? (
        <div className="relative mb-6">
          <div className="h-32 overflow-hidden rounded-2xl border border-border/60 bg-muted/30 sm:h-40">
            <div className="absolute inset-0" style={buildCoverStyle(theme)} />
          </div>
          {icon ? (
            <div
              className={cn(
                'absolute -bottom-5 rounded-2xl border border-border/60 bg-background/90 px-3 py-1.5 text-2xl shadow-sm backdrop-blur',
                iconAlignment,
              )}
              aria-hidden="true"
            >
              {icon}
            </div>
          ) : null}
        </div>
      ) : icon ? (
        <div
          className={cn(
            'flex',
            iconContainerAlignment,
          )}
        >
          <div
            className="rounded-2xl border border-border/60 bg-background/90 px-3 py-1.5 text-2xl shadow-sm"
            aria-hidden="true"
          >
            {icon}
          </div>
        </div>
      ) : null}
    </div>
  );
}
